/*
 * Nibby product page blocks (vanilla JS, no dependencies). Loaded once by snippets/nibby-product-assets.liquid.
 *
 * Shared state per main-product section: { variantId, planId }.
 * - variantId follows Dawn: PUB_SUB_EVENTS.variantChange, published by assets/product-info.js after it has
 *   re-rendered the section (bundle picker included) and updated the hidden `id` input.
 * - planId follows <nibby-purchase-options> ('' = one-time purchase).
 * Every change dispatches document event 'nibby:buybox-change' ({ sectionId, variantId, planId }).
 * <nibby-purchase-options> also dispatches 'nibby:subscription-change'
 * ({ sectionId, subscribed, plan, price, priceCents, percent }) and the older 'nibby:purchase-change'.
 *
 * Elements:
 * <nibby-buybox>            invisible controller with all variant/plan prices (from Liquid, pre-formatted).
 *                           Updates the live price in Dawn's add to cart button, the bundle card prices and the
 *                           "You're saving X%" pill.
 * <nibby-purchase-options>  one-time vs subscribe (cards or checkbox style); writes the hidden selling_plan input.
 * <nibby-dog-size>          dog size → recommendation, optional auto-select of the matching bundle.
 * <nibby-tabs>              accessible tabs (arrow keys, Home, End).
 * <nibby-free-gifts>        locked / unlocked gifts.
 * <nibby-delivery-info>     stock line + estimated delivery dates (business days).
 * <nibby-sticky-atc>        sticky add to cart bar; clicks Dawn's #ProductSubmitButton-{section}.
 */
(function () {
  if (window.NibbyBuybox) return;

  const canSubscribe = () =>
    typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange;

  const readJson = (root, selector) => {
    try {
      return JSON.parse(root.querySelector(selector).textContent);
    } catch (error) {
      return null;
    }
  };

  const productForm = (sectionId) => document.getElementById(`product-form-${sectionId}`);

  /* ---------- Shared state ---------- */
  const states = {};
  const dataCache = {};

  const NibbyBuybox = {
    data(sectionId) {
      if (dataCache[sectionId]) return dataCache[sectionId];
      const host = document.querySelector(`nibby-buybox[data-section-id="${sectionId}"]`);
      const data = host ? readJson(host, '[data-buybox-data]') : null;
      if (data) dataCache[sectionId] = data;
      return data;
    },
    state(sectionId) {
      if (!states[sectionId]) {
        const form = productForm(sectionId);
        const idInput = form && form.querySelector('input[name="id"]');
        states[sectionId] = { variantId: idInput ? idInput.value : '', planId: '' };
      }
      return states[sectionId];
    },
    set(sectionId, patch) {
      Object.assign(this.state(sectionId), patch);
      document.dispatchEvent(
        new CustomEvent('nibby:buybox-change', { detail: Object.assign({ sectionId }, this.state(sectionId)) })
      );
    },
    variant(sectionId) {
      const data = this.data(sectionId);
      const state = this.state(sectionId);
      return data && data.variants[state.variantId] ? data.variants[state.variantId] : null;
    },
    /* Price of the current choice: { cents, text } (subscription price when a plan is chosen). */
    price(sectionId) {
      const variant = this.variant(sectionId);
      if (!variant) return null;
      const plan = this.state(sectionId).planId && variant.plans[this.state(sectionId).planId];
      return plan ? { cents: plan.price, text: plan.priceText } : { cents: variant.price, text: variant.priceText };
    },
    formatMoney(sectionId, cents) {
      const data = this.data(sectionId);
      try {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: (data && data.currency) || 'USD' }).format(
          cents / 100
        );
      } catch (error) {
        return (cents / 100).toFixed(2);
      }
    },
  };
  window.NibbyBuybox = NibbyBuybox;

  const onBuyboxChange = (sectionId, callback) => {
    const handler = (event) => {
      if (event.detail.sectionId === sectionId) callback(event.detail);
    };
    document.addEventListener('nibby:buybox-change', handler);
    return () => document.removeEventListener('nibby:buybox-change', handler);
  };

  // Dawn variant change → shared state. Purchase options first adjusts its plan for the new variant.
  const listenToDawn = () => {
    if (!canSubscribe()) return;
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      if (!data || !data.variant) return;
      const sectionId = data.sectionId;
      const known = NibbyBuybox.data(sectionId);
      if (!known || !known.variants[String(data.variant.id)]) return; // another product (e.g. quick add)
      NibbyBuybox.state(sectionId).variantId = String(data.variant.id);
      const purchase = document.querySelector(`nibby-purchase-options[data-section-id="${sectionId}"]`);
      if (purchase && typeof purchase.updateVariant === 'function') {
        purchase.updateVariant(String(data.variant.id)); // dispatches nibby:buybox-change
      } else {
        NibbyBuybox.set(sectionId, {});
      }
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', listenToDawn);
  } else {
    listenToDawn();
  }

  /* ---------- Purchase options ---------- */
  customElements.define(
    'nibby-purchase-options',
    class NibbyPurchaseOptions extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.data = readJson(this, '[data-nibby-purchase-data]');
        this.hiddenInput = this.querySelector('[data-selling-plan-input]');
        this.isCheckbox = this.dataset.style === 'checkbox';
        this.variantId = NibbyBuybox.state(this.sectionId).variantId;
        this.onChange = this.onChange.bind(this);
        this.addEventListener('change', this.onChange);

        if (this.currentVariantData()) {
          this.groups.forEach((option) => this.updateGroup(option));
        }
        this.sync();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
      }

      get inputs() {
        return Array.from(this.querySelectorAll('.nibby-purchase__input, [data-sub-checkbox]'));
      }

      get groups() {
        return Array.from(this.querySelectorAll('[data-option][data-group-id]'));
      }

      onChange(event) {
        const target = event.target;
        if (target.matches('[data-plan-select]')) {
          const option = target.closest('[data-option]');
          const input = option.querySelector('[data-group-input]');
          input.value = target.value;
          this.updateGroup(option);
          if (!input.disabled) input.checked = true;
        }
        this.sync();
      }

      currentPlanId() {
        if (this.isCheckbox) {
          const box = this.querySelector('[data-sub-checkbox]');
          return box && box.checked && !box.disabled ? box.value : '';
        }
        const checked = this.inputs.find((input) => input.checked);
        return checked ? checked.value : '';
      }

      currentVariantData() {
        if (!this.data || !this.variantId) return null;
        return this.data.variants[this.variantId] || null;
      }

      /* Kept for the sticky bar / older listeners. */
      getState() {
        const planId = this.currentPlanId();
        const variant = this.currentVariantData();
        const plan = variant && planId ? variant.plans[planId] : null;
        return {
          sectionId: this.sectionId,
          sellingPlanId: planId,
          subscribed: Boolean(plan),
          plan: planId,
          priceText: plan ? plan.price : variant ? variant.price : '',
          price: plan ? plan.price : variant ? variant.price : '',
          priceCents: plan ? plan.priceCents : variant ? variant.priceCents : null,
          percent: plan ? plan.saving : 0,
        };
      }

      /* Reflect the choice in the hidden input, the card states and the shared state. */
      sync() {
        const planId = this.currentPlanId();

        this.querySelectorAll('[data-option]').forEach((option) => {
          const input = option.querySelector('input');
          option.classList.toggle('is-selected', Boolean(input && input.checked && !input.disabled));
        });

        if (this.hiddenInput) {
          this.hiddenInput.value = planId;
          this.hiddenInput.disabled = planId === '';
        }

        const state = this.getState();
        document.dispatchEvent(new CustomEvent('nibby:purchase-change', { detail: state }));
        document.dispatchEvent(
          new CustomEvent('nibby:subscription-change', {
            detail: {
              sectionId: state.sectionId,
              subscribed: state.subscribed,
              plan: state.plan,
              price: state.price,
              priceCents: state.priceCents,
              percent: state.percent,
            },
          })
        );
        NibbyBuybox.set(this.sectionId, { planId: state.subscribed ? planId : '' });
      }

      updateVariant(variantId) {
        if (!this.data || !this.data.variants[variantId]) return;
        this.variantId = variantId;
        const variant = this.currentVariantData();

        const oncePrice = this.querySelector('[data-once-price]');
        if (oncePrice) oncePrice.textContent = variant.price;

        this.groups.forEach((option) => this.updateGroup(option));

        // If the chosen plan is not sold with this variant, fall back to one-time (cards) or untick (checkbox).
        const checked = this.inputs.find((input) => input.checked);
        if (checked && checked.disabled) {
          checked.checked = false;
          if (!this.isCheckbox) {
            const fallback =
              this.inputs.find((input) => input.value === '') ||
              this.inputs.find((input) => input.hasAttribute('data-group-input') && !input.disabled);
            if (fallback) fallback.checked = true;
          }
        }
        this.sync();
      }

      updateGroup(option) {
        const variant = this.currentVariantData();
        const input = option.querySelector('[data-group-input]');
        if (!variant || !input) return;

        // Stay on a subscription when this pack lacks the chosen frequency: use the first frequency it offers.
        const select = option.querySelector('[data-plan-select]');
        if (select) {
          Array.from(select.options).forEach((item) => {
            item.disabled = !variant.plans[item.value];
          });
          if (!variant.plans[input.value]) {
            const fallback = Array.from(select.options).find((item) => !item.disabled);
            if (fallback) {
              select.value = fallback.value;
              input.value = fallback.value;
            }
          }
        }

        // Checkbox style: packs may each have their own plan (e.g. 3 Packs every 3 months). Switch to the
        // plan this variant is sold with.
        if (this.isCheckbox && !select && !variant.plans[input.value]) {
          const first = Object.keys(variant.plans)[0];
          if (first) input.value = first;
        }

        const plan = variant.plans[input.value];
        input.disabled = !plan;

        const priceEl = option.querySelector('[data-plan-price]');
        if (priceEl) priceEl.textContent = plan ? plan.price : this.data.unavailableText || '';

        const showSaving = Boolean(plan && plan.saving > 0);
        const compareEl = option.querySelector('[data-plan-compare]');
        if (compareEl) {
          compareEl.textContent = plan ? plan.compare : '';
          compareEl.hidden = !showSaving;
        }
        const savingEl = option.querySelector('[data-plan-saving]');
        if (savingEl) {
          savingEl.textContent = showSaving ? (this.data.savingText || '').replace('[percent]', plan.saving) : '';
          savingEl.hidden = !showSaving;
        }
        const titleEl = option.querySelector('[data-sub-title]');
        if (titleEl) {
          titleEl.textContent = showSaving
            ? titleEl.dataset.template.replace('[percent]', plan.saving)
            : titleEl.dataset.fallback;
        }

        const planInfo = this.data.plans && this.data.plans[input.value];
        const termsEl = option.querySelector('[data-plan-terms]');
        if (termsEl && planInfo) termsEl.textContent = planInfo.terms;
      }
    }
  );

  /* ---------- Buy box controller: button price, bundle cards, saving pill ---------- */
  customElements.define(
    'nibby-buybox',
    class NibbyBuyboxElement extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        delete dataCache[this.sectionId]; // theme editor re-render: read fresh data
        this.off = onBuyboxChange(this.sectionId, () => this.render());
        this.onQuantity = (event) => {
          const form = productForm(this.sectionId);
          if (event.target.name === 'quantity' && form && (form.contains(event.target) || event.target.form === form)) {
            this.render();
          }
        };
        document.addEventListener('change', this.onQuantity);
        const purchase = document.querySelector(`nibby-purchase-options[data-section-id="${this.sectionId}"]`);
        if (purchase && typeof purchase.getState === 'function') {
          const state = purchase.getState();
          NibbyBuybox.state(this.sectionId).planId = state.subscribed ? state.plan : '';
        }
        this.render();
      }

      disconnectedCallback() {
        if (this.off) this.off();
        document.removeEventListener('change', this.onQuantity);
      }

      quantity() {
        const input = document.querySelector(`#Quantity-${this.sectionId}, #product-form-${this.sectionId} [name="quantity"]`);
        const value = input ? parseInt(input.value, 10) : 1;
        return value > 0 ? value : 1;
      }

      render() {
        this.renderButtonPrice();
        this.renderBundles();
      }

      renderButtonPrice() {
        const button = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        const price = NibbyBuybox.price(this.sectionId);
        if (!button || !price) return;
        let span = button.querySelector('[data-nibby-atc-price]');
        if (!span) {
          // Dawn rewrites only the first <span> (submitButtonText) on variant change, so this sibling survives.
          span = document.createElement('span');
          span.className = 'nibby-atc-price';
          span.setAttribute('data-nibby-atc-price', '');
          const label = button.querySelector('span');
          if (label) label.after(span);
          else button.prepend(span);
        }
        const qty = this.quantity();
        span.textContent = qty > 1 ? NibbyBuybox.formatMoney(this.sectionId, price.cents * qty) : price.text;
      }

      renderBundles() {
        const data = NibbyBuybox.data(this.sectionId);
        const state = NibbyBuybox.state(this.sectionId);
        if (!data) return;
        document.querySelectorAll(`.nibby-bundle-wrap[data-section-id="${this.sectionId}"]`).forEach((wrap) => {
          const bundle = readJson(wrap, '[data-bundle-data]');
          if (!bundle) return;

          wrap.querySelectorAll('.nibby-bundle__card[data-variant-id]').forEach((card) => {
            const id = card.dataset.variantId;
            const variant = data.variants[id];
            if (!variant) return;
            // When subscribed, a pack with its own plan (e.g. every 3 months) shows that plan's price.
            let planKey = state.planId && variant.plans[state.planId] ? state.planId : '';
            if (state.planId && !planKey) planKey = Object.keys(variant.plans)[0] || '';
            const plan = planKey ? variant.plans[planKey] : null;
            const cents = plan ? plan.price : variant.price;

            const priceEl = card.querySelector('[data-bundle-price]');
            if (priceEl) priceEl.textContent = plan ? plan.priceText : variant.priceText;

            // Struck-through price: the real compare-at price, or the one-time price when subscribed.
            let compareText = '';
            if (variant.compare > cents) compareText = variant.compareText;
            else if (plan && variant.price > cents) compareText = variant.priceText;
            const compareEl = card.querySelector('[data-bundle-compare]');
            const compareValue = card.querySelector('[data-bundle-compare-value]');
            if (compareEl && compareValue) {
              compareValue.textContent = compareText;
              compareEl.hidden = compareText === '';
            }

            const saveEl = card.querySelector('[data-bundle-save]');
            if (saveEl) {
              const pct = variant.compare > cents ? Math.round(((variant.compare - cents) / variant.compare) * 100) : 0;
              saveEl.textContent = (saveEl.dataset.template || '').replace('[percent]', pct);
              saveEl.hidden = pct < 1 || !saveEl.dataset.template;
            }

            const perDayEl = card.querySelector('[data-bundle-per-day]');
            const perDay = bundle.variants[id] && bundle.variants[id].perDay;
            if (perDayEl && perDay) {
              perDayEl.textContent = (plan && perDay[planKey]) || perDay[''] || perDayEl.textContent;
            }
          });

          // Gift rows in the cards are unlocked while Subscribe & save is selected.
          wrap.classList.toggle('is-subscribed', Boolean(state.planId));

          const pill = wrap.querySelector('[data-bundle-saving]');
          const pillText = wrap.querySelector('[data-bundle-saving-text]');
          const current = bundle.variants[state.variantId];
          const price = NibbyBuybox.price(this.sectionId);
          // Saving vs the struck-through compare-at price when there is one, so the pill matches the card.
          const variantData = data.variants[state.variantId];
          const reference = Math.max(current ? current.base : 0, variantData && variantData.compare ? variantData.compare : 0);
          if (pill && pillText && current && price && reference > 0) {
            const percent = Math.round(((reference - price.cents) / reference) * 100);
            pillText.textContent = pillText.dataset.template.replace('[percent]', percent);
            pill.hidden = percent < 1;
          }
        });
      }
    }
  );

  /* ---------- Dog size ---------- */
  customElements.define(
    'nibby-dog-size',
    class NibbyDogSize extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.result = this.querySelector('[data-dog-size-result]');
        this.property = this.querySelector('[data-dog-size-property]');
        this.addEventListener('change', (event) => {
          if (!event.target.matches('.nibby-dog-size__input')) return;
          this.apply(event.target, true);
          try {
            sessionStorage.setItem(this.dataset.storageKey, event.target.value);
          } catch (error) {
            /* storage unavailable: selection is simply not remembered */
          }
        });

        let saved = null;
        try {
          saved = sessionStorage.getItem(this.dataset.storageKey);
        } catch (error) {
          saved = null;
        }
        if (saved) {
          const input = Array.from(this.querySelectorAll('.nibby-dog-size__input')).find((item) => item.value === saved);
          if (input) {
            input.checked = true;
            this.apply(input, false); // restore only; never changes the pack on page load
          }
        }
      }

      apply(input, selectPack) {
        const chews = input.dataset.chews || '';
        const match = chews.replace(/[\[\]]/g, '').match(/\d+(\.\d+)?/);
        const perTub = parseFloat(this.dataset.daysPerTub) || 0;
        let text = '';
        if (match && parseFloat(match[0]) > 0 && perTub > 0) {
          const days = Math.round(perTub / parseFloat(match[0]));
          text = (this.dataset.resultTemplate || '').replace('[chews]', chews).replace('[days]', days);
        } else {
          text = (this.dataset.resultTemplateShort || '').replace('[chews]', chews);
        }
        if (this.result) this.result.textContent = text;

        if (this.property) {
          this.property.value = input.value;
          this.property.disabled = false;
        }

        if (selectPack && this.dataset.autoSelect === 'true') {
          const position = parseInt(input.dataset.pack, 10);
          const radios = document.querySelectorAll(
            `#variant-selects-${this.sectionId} .nibby-bundle__option input[type="radio"]`
          );
          const radio = position > 0 ? radios[position - 1] : null;
          // A real click on Dawn's radio, so VariantSelects + ProductInfo run exactly as for a shopper's tap.
          if (radio && !radio.checked) radio.click();
        }
      }
    }
  );

  /* ---------- Tabs ---------- */
  customElements.define(
    'nibby-tabs',
    class NibbyTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => this.select(tab, false));
          tab.addEventListener('keydown', (event) => this.onKey(event, tab));
        });
      }

      onKey(event, tab) {
        const index = this.tabs.indexOf(tab);
        let next = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = this.tabs[(index + 1) % this.tabs.length];
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp')
          next = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
        if (event.key === 'Home') next = this.tabs[0];
        if (event.key === 'End') next = this.tabs[this.tabs.length - 1];
        if (!next) return;
        event.preventDefault();
        this.select(next, true);
      }

      select(tab, focus) {
        this.tabs.forEach((item) => {
          const selected = item === tab;
          item.setAttribute('aria-selected', String(selected));
          item.tabIndex = selected ? 0 : -1;
          const panel = document.getElementById(item.getAttribute('aria-controls'));
          if (panel) panel.hidden = !selected;
        });
        if (focus) tab.focus();
      }
    }
  );

  /* ---------- Free gifts ---------- */
  customElements.define(
    'nibby-free-gifts',
    class NibbyFreeGifts extends HTMLElement {
      connectedCallback() {
        if (this.dataset.unlockMode === 'always') return;
        this.sectionId = this.dataset.sectionId;
        this.caption = this.querySelector('[data-gifts-caption-text]');
        this.heading = this.querySelector('[data-gifts-heading]');
        this.firstGift = this.querySelector('[data-gift-first]');
        this.off = onBuyboxChange(this.sectionId, (state) => this.render(Boolean(state.planId), state.variantId));
        const start = NibbyBuybox.state(this.sectionId);
        this.render(Boolean(start.planId), start.variantId);
      }

      disconnectedCallback() {
        if (this.off) this.off();
      }

      render(unlocked, variantId) {
        this.classList.toggle('is-locked', !unlocked);
        this.classList.toggle('is-unlocked', unlocked);
        // Optional: gift 1 is not included with the first pack option (e.g. 1 Pack = e-book only).
        const single = Boolean(this.dataset.firstVariant) && String(variantId) === this.dataset.firstVariant;
        if (this.firstGift) this.firstGift.hidden = single;
        if (this.heading) {
          const d = this.heading.dataset;
          const text = single
            ? (unlocked ? this.dataset.headingUnlockedSingle : this.dataset.headingSingle)
            : (unlocked ? d.unlocked : d.locked);
          this.heading.textContent = text || (unlocked ? d.unlocked : d.locked) || '';
        }
        if (this.caption) {
          const text = unlocked ? this.caption.dataset.unlocked : this.caption.dataset.locked;
          this.caption.textContent = text || '';
          this.caption.parentElement.hidden = !text;
        }
      }
    }
  );

  /* ---------- Delivery info ---------- */
  customElements.define(
    'nibby-delivery-info',
    class NibbyDeliveryInfo extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.renderDates();
        this.stock = this.querySelector('[data-delivery-stock]');
        this.off = onBuyboxChange(this.sectionId, () => {
          const variant = NibbyBuybox.variant(this.sectionId);
          if (this.stock && variant) this.stock.hidden = !variant.available;
        });
      }

      disconnectedCallback() {
        if (this.off) this.off();
      }

      static addBusinessDays(start, days) {
        const date = new Date(start.getFullYear(), start.getMonth(), start.getDate());
        let added = 0;
        while (added < days) {
          date.setDate(date.getDate() + 1);
          const day = date.getDay();
          if (day !== 0 && day !== 6) added += 1;
        }
        return date;
      }

      renderDates() {
        const line = this.querySelector('[data-delivery-date]');
        const target = this.querySelector('[data-delivery-date-text]');
        if (!line || !target) return;
        const min = Math.max(0, parseInt(this.dataset.minDays, 10) || 0);
        const max = Math.max(min, parseInt(this.dataset.maxDays, 10) || min);
        const now = new Date();
        const format = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
        const first = format.format(NibbyDeliveryInfo.addBusinessDays(now, min));
        const last = format.format(NibbyDeliveryInfo.addBusinessDays(now, max));
        target.textContent = (line.dataset.template || '').replace('[date1]', first).replace('[date2]', last);
        line.classList.add('is-ready');
      }
    }
  );

  /* ---------- Sticky add to cart ---------- */
  customElements.define(
    'nibby-sticky-atc',
    class NibbyStickyAtc extends HTMLElement {
      connectedCallback() {
        const sectionId = this.dataset.sectionId;

        // Not wanted inside a quick-add modal.
        if (this.closest('quick-add-modal')) {
          this.remove();
          return;
        }

        // Escape transformed ancestors (Dawn's scroll animations) so position: fixed is relative to the viewport.
        if (this.parentElement !== document.body) {
          document.body
            .querySelectorAll(`:scope > nibby-sticky-atc[data-section-id="${sectionId}"]`)
            .forEach((old) => old !== this && old.remove());
          document.body.appendChild(this);
          return; // connectedCallback runs again after the move
        }

        if (this.initialised) return;
        this.initialised = true;
        this.sectionId = sectionId;

        this.data = readJson(this, '[data-sticky-data]') || { variants: {}, labels: {} };
        this.button = this.querySelector('[data-sticky-button]');
        this.label = this.querySelector('[data-sticky-label]');
        this.priceEl = this.querySelector('[data-sticky-price]');
        this.variantEl = this.querySelector('[data-sticky-variant]');

        this.button.addEventListener('click', () => this.addToCart());
        this.off = onBuyboxChange(sectionId, () => this.render());

        // Theme editor: drop this bar when its section is re-rendered or removed.
        this.onSectionUnload = (event) => {
          if (event.detail && event.detail.sectionId === sectionId) this.remove();
        };
        document.addEventListener('shopify:section:unload', this.onSectionUnload);

        this.render();
        this.observe();
      }

      disconnectedCallback() {
        if (this.parentElement === document.body) return;
        if (this.off) this.off();
        if (this.observer) this.observer.disconnect();
        document.removeEventListener('shopify:section:unload', this.onSectionUnload);
        document.body.classList.remove('nibby-has-sticky-atc');
      }

      observe() {
        const target = productForm(this.sectionId);
        if (!target || !('IntersectionObserver' in window)) return;
        this.observer = new IntersectionObserver((entries) => {
          const entry = entries[0];
          // Show only once the buy button has scrolled above the viewport.
          this.setVisible(!entry.isIntersecting && entry.boundingClientRect.bottom < 0);
        });
        this.observer.observe(target);
      }

      setVisible(visible) {
        this.classList.toggle('is-visible', visible);
        this.setAttribute('aria-hidden', String(!visible));
        this.toggleAttribute('inert', !visible);
        // Reserve room at the bottom of the page so the bar never hides the footer / disclaimer.
        document.body.classList.toggle('nibby-has-sticky-atc', visible && getComputedStyle(this).display !== 'none');
      }

      render() {
        const state = NibbyBuybox.state(this.sectionId);
        const variant = this.data.variants[state.variantId];
        if (variant) {
          if (this.variantEl) this.variantEl.textContent = variant.title;
          this.button.disabled = !variant.available;
          this.label.textContent = variant.available ? this.data.labels.add : this.data.labels.soldOut;
        }
        const price = NibbyBuybox.price(this.sectionId);
        if (price && this.priceEl) this.priceEl.textContent = price.text;
      }

      addToCart() {
        const submit = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (!submit || submit.disabled || submit.getAttribute('aria-disabled') === 'true') return;
        submit.click();
      }
    }
  );
})();

/*
 * Product gallery helper (Pet Gently-style): only for Dawn's "Thumbnail carousel" layout.
 * Desktop: thumbnails become a vertical column with up/down buttons, the main image gets prev/next arrows.
 * The arrows just click the neighbouring Dawn thumbnail, so Dawn's own media-gallery logic stays in charge.
 */
(() => {
  // From 750px Dawn shows one active image (no swipe track), so the arrows step through the thumbnails.
  const STEP_MODE = window.matchMedia('(min-width: 750px)');

  const makeButton = (className, label, direction) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `nibby-gallery-btn ${className}`;
    button.setAttribute('aria-label', label);
    button.dataset.direction = direction;
    button.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    return button;
  };

  const visibleThumbs = (gallery) =>
    Array.from(gallery.querySelectorAll('.thumbnail-list__item')).filter((item) => item.offsetParent !== null);

  const step = (gallery, direction) => {
    const items = visibleThumbs(gallery);
    if (!items.length) return;
    let index = items.findIndex((item) => item.querySelector('.thumbnail[aria-current="true"]'));
    if (index < 0) index = 0;
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const next = items[target];
    const button = next.querySelector('.thumbnail');
    if (button) button.click();
    next.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  };

  const init = (gallery) => {
    if (gallery.dataset.nibbyGallery) return;
    gallery.dataset.nibbyGallery = 'true';
    const thumbs = gallery.querySelector('.thumbnail-slider');
    const viewer = gallery.querySelector('slider-component[id^="GalleryViewer"]');
    if (!thumbs || !viewer) return;

    const list = thumbs.querySelector('.thumbnail-list');
    const up = makeButton('nibby-gallery-btn--up', 'Scroll thumbnails up', '-1');
    const down = makeButton('nibby-gallery-btn--down', 'Scroll thumbnails down', '1');
    thumbs.append(up, down);
    [up, down].forEach((button) =>
      button.addEventListener('click', () => {
        list.scrollBy({ top: Number(button.dataset.direction) * list.clientHeight * 0.7, behavior: 'smooth' });
      })
    );

    const prev = makeButton('nibby-gallery-btn--prev', 'Previous image', '-1');
    const next = makeButton('nibby-gallery-btn--next', 'Next image', '1');
    viewer.append(prev, next);
    const track = viewer.querySelector('.product__media-list');

    // Hide the arrow that has nowhere to go: no "previous" on the first image, no "next" on the last.
    const updateArrows = () => {
      let atStart;
      let atEnd;
      if (STEP_MODE.matches) {
        const items = visibleThumbs(gallery);
        const index = items.findIndex((item) => item.querySelector('.thumbnail[aria-current="true"]'));
        atStart = index <= 0;
        atEnd = index < 0 || index >= items.length - 1;
      } else if (track) {
        atStart = track.scrollLeft <= 2;
        atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
      }
      prev.hidden = Boolean(atStart);
      next.hidden = Boolean(atEnd);
    };

    [prev, next].forEach((button) =>
      button.addEventListener('click', () => {
        const direction = Number(button.dataset.direction);
        if (STEP_MODE.matches) {
          step(gallery, direction);
          window.setTimeout(updateArrows, 50);
          return;
        }
        // Mobile: the main images are Dawn's swipe slider; scroll it one slide.
        if (!track) return;
        track.scrollTo({ left: track.scrollLeft + direction * track.clientWidth, behavior: 'smooth' });
      })
    );
    if (track) track.addEventListener('scroll', () => window.requestAnimationFrame(updateArrows), { passive: true });
    thumbs.addEventListener('click', () => window.setTimeout(updateArrows, 50));
    STEP_MODE.addEventListener('change', updateArrows);
    window.addEventListener('load', updateArrows);
    updateArrows();
  };

  const run = () => document.querySelectorAll('.product--thumbnail_slider media-gallery').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();

/* Lightbox on mobile: all photos side by side, swipe left/right; open on the tapped photo (2026-10-13). */
(function () {
  const mobile = window.matchMedia('(max-width: 749px)');
  const patch = () => {
    const Modal = customElements.get('product-modal');
    if (!Modal || Modal.prototype.nibbySwipePatched) return;
    const original = Modal.prototype.showActiveMedia;
    Modal.prototype.showActiveMedia = function () {
      original.call(this);
      if (!mobile.matches) return;
      const container = this.querySelector('.product-media-modal__content');
      const active = this.querySelector('.product-media-modal__content > .active');
      if (!container || !active) return;
      const items = Array.from(container.children);
      const index = items.indexOf(active);
      requestAnimationFrame(() => {
        container.scrollTo({ left: index * container.clientWidth, behavior: 'instant' });
      });
    };
    Modal.prototype.nibbySwipePatched = true;
  };
  if (customElements.get('product-modal')) patch();
  else customElements.whenDefined('product-modal').then(patch);
})();
